/**
 * ============================================================================
 * ActionItemRow — single row of the ReviewMinutes action-items list.
 * Owner: Phase 7 — Management Review Module (Agent F).
 * URS rows: MGR-005, MGR-007.
 * ============================================================================
 */

import { useTranslation } from 'react-i18next';
import styles from './ReviewMinutes.module.css';
import type { ActionItem } from './useReviewDetailQuery';

const STATUS_CLASS: Record<ActionItem['status'], string> = {
  OPEN: styles.statusOpen!,
  IN_PROGRESS: styles.statusInProgress!,
  COMPLETED: styles.statusCompleted!,
  CLOSED: styles.statusClosed!,
  CANCELLED: styles.statusCancelled!,
};

export interface ActionItemRowProps {
  action: ActionItem;
  onClose?: (id: string) => void;
}

export function ActionItemRow({ action, onClose }: ActionItemRowProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });
  return (
    <li className={styles.actionRow} aria-label={`Action ${action.actionNumber}`}>
      <div className={styles.actionRowHeader}>
        <span className={styles.actionNumber}>{action.actionNumber}</span>
        <span className={STATUS_CLASS[action.status]}>{action.status}</span>
      </div>
      <div className={styles.actionTitle}>{action.title}</div>
      <div className={styles.actionMeta}>
        {t('mgr.minutes.due', { defaultValue: 'Due' })}:{' '}
        {dateFmt.format(new Date(action.dueUtc))} ·{' '}
        {t('mgr.minutes.owner', { defaultValue: 'Owner' })}:{' '}
        {action.ownerUserId.slice(0, 8)}…
      </div>
      {action.capaId && (
        <a
          className={styles.capaLink}
          href={`#/capa/${encodeURIComponent(action.capaId)}`}
          aria-label={`Linked CAPA ${action.capaId}`}
        >
          CAPA · {action.capaId.slice(0, 8)}…
        </a>
      )}
      {onClose && !['CLOSED', 'CANCELLED', 'COMPLETED'].includes(action.status) && (
        <button
          type="button"
          className={styles.actionMeta}
          onClick={() => onClose(action.id)}
        >
          {t('mgr.minutes.actions.close', { defaultValue: 'Close action' })}
        </button>
      )}
    </li>
  );
}
